import { Loader, HelpCircle, X, Search, ExternalLink } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Source } from "../lib/types";
import { useAuth } from '../contexts/AuthContext';
import { generateMdx } from "../lib/markdown";
import { sourceTypeFields, sourceTypes } from "../lib/sources";
import { AUTH_PROVIDERS } from "../lib/authProviders";
import { runOAuthPopup } from "../lib/popup";
import { useToast } from "../contexts/ToastContext";
import { usePanel } from "../contexts/PanelContext";
import SourceBrowser from "./SourceBrowser";
import SourceLogo from "./SourceLogo";
import { InlineError } from "./InlineError";

function encodeTypeFilter(doc: boolean, web: boolean): string | null {
    if (doc && web) return null;
    if (doc) return 'document_source';
    if (web) return 'web_application';
    return 'none';
}

function SourceIcon({ id, label }: { id: string; label: string }) {
    return (
        <div className="w-10 h-10 rounded-[10px] border border-softgrey bg-white flex items-center justify-center shrink-0">
            <SourceLogo type={id} size="lg" alt={label} />
        </div>
    );
}

function SourceTypeCombobox({ items, selected, onSelect, onClear, getDisabled, typeFilter, onTypeFilterChange }: {
    items: { id: string; type: string; label: string; comingSoon?: boolean }[];
    selected: string;
    onSelect: (id: string) => void;
    onClear: () => void;
    getDisabled: (id: string) => { reason: string; href?: string } | null;
    typeFilter?: string | null;
    onTypeFilterChange: (next: string | null) => void;
}) {
    const [query, setQuery] = useState('');
    // The URL is the source of truth; checkbox state derives from `typeFilter`.
    const showDocumentSource = !typeFilter || typeFilter === 'document_source';
    const showWebApplication = !typeFilter || typeFilter === 'web_application';
    const setShowDocumentSource = (checked: boolean) => {
        onTypeFilterChange(encodeTypeFilter(checked, showWebApplication));
    };
    const setShowWebApplication = (checked: boolean) => {
        onTypeFilterChange(encodeTypeFilter(showDocumentSource, checked));
    };
    const isSelected = items.some(i => i.id === selected);

    const filtered = items
        .filter(t => {
            if (t.type === 'document_source') return showDocumentSource;
            if (t.type === 'web_application') return showWebApplication;
            return true;
        })
        .filter(t => t.label.toLowerCase().includes(query.toLowerCase()))
        .sort((a, b) => {
            const disabledDiff = Number(!!getDisabled(a.id)) - Number(!!getDisabled(b.id));
            if (disabledDiff !== 0) return disabledDiff;
            return a.label.localeCompare(b.label);
        });

    if (isSelected) {
        const item = items.find(i => i.id === selected)!;
        return (
            <div className="mb-6">
                <div className="flex items-center gap-3.5 p-4 border border-brand/30 bg-brand/3 rounded-[10px]">
                    <SourceIcon id={item.id} label={item.label} />
                    <span className="flex-1 text-[15px] font-medium text-graphite">{item.label}</span>
                    <button
                        type="button"
                        onClick={onClear}
                        className="p-1 text-coolslate hover:text-graphite cursor-pointer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-6">
            <div className="relative mb-3">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-coolslate" />
                <input
                    autoFocus
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search by name"
                    className="w-full text-[15px] pl-11 pr-4 py-3.5 border border-softgrey rounded-[10px] bg-white text-graphite focus:outline-none focus:border-brand placeholder:text-coolslate"
                />
            </div>
            <div className="flex items-center gap-5 mb-5">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={showDocumentSource}
                        onChange={e => setShowDocumentSource(e.target.checked)}
                        className="w-4 h-4 rounded border-softgrey text-brand focus:ring-brand cursor-pointer"
                    />
                    <span className="text-sm text-graphite">Document Sources</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={showWebApplication}
                        onChange={e => setShowWebApplication(e.target.checked)}
                        className="w-4 h-4 rounded border-softgrey text-brand focus:ring-brand cursor-pointer"
                    />
                    <span className="text-sm text-graphite">Web Applications</span>
                </label>
            </div>
            {<div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                {filtered.length === 0 ? (
                    <p className="col-span-2 text-center py-6 text-sm text-coolslate">No matches</p>
                ) : filtered.map(type => {
                    const disabled = getDisabled(type.id);
                    return (
                        <button
                            key={type.id}
                            type="button"
                            disabled={!!disabled}
                            onClick={() => { onSelect(type.id); setQuery(''); }}
                            title={disabled?.reason || undefined}
                            className={`flex items-center gap-3.5 px-4 py-4 rounded-[10px] border transition-colors text-left ${
                                disabled
                                    ? 'border-softgrey bg-white opacity-50 cursor-not-allowed'
                                    : 'border-softgrey bg-white hover:border-brand/40 hover:shadow-sm cursor-pointer'
                            }`}
                        >
                            <SourceIcon id={type.id} label={type.label} />
                            <div className="flex-1 min-w-0">
                                <span className="text-[15px] font-medium text-graphite block truncate">{type.label}</span>
                                {disabled && (
                                    disabled.href ? (
                                        <a href={disabled.href} className="text-[11px] text-brand hover:underline" onClick={e => e.stopPropagation()}>{disabled.reason}</a>
                                    ) : (
                                        <span className="text-[11px] text-coolslate">{disabled.reason}</span>
                                    )
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>}
        </div>
    );
}

function HelpPanelContent({ documentation }: {
    documentation: string;
}) {
    const [renderedContent, setRenderedContent] = useState('');

    useEffect(() => {
        setRenderedContent(generateMdx(documentation));
    }, [documentation]);

    return (
        <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
    );
}

interface SourceFormProps {
    editSource?: Source | null;
    onSuccess?: () => void;
}

export default function SourceForm({ editSource = null, onSuccess }: SourceFormProps) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const typeFilter = searchParams.get('type');
    const updateTypeFilter = (next: string | null) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            if (next === null) params.delete('type');
            else params.set('type', next);
            return params;
        }, { replace: true });
    };
    const redirectTo = searchParams.get('redirect');
    const { authFetch } = useAuth();
    const { showToast } = useToast();
    const [error, setError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    // ?source=<type> opens the form for that type, as a link from the agent does.
    const askedSource = searchParams.get('source') ?? '';
    const offered = (id: string) => sourceTypes.some(t => t.id === id && !t.comingSoon);
    const [sourceType, setSourceType] = useState(() => !editSource && offered(askedSource) ? askedSource : '');
    const { openPanel } = usePanel();
    const isEditMode = !!editSource;

    type Credentials = { token: string; refresh_token: string; scopes?: string[] };
    type ProviderConfig = {
        folder_id?: string;
        folder_name?: string;
        site_id?: string;
        site_name?: string;
        drive_id?: string;
        drive_name?: string;
        base_uri?: string;
    };

    const [credentials, setCredentials] = useState<Credentials | null>(null);
    const [providerConfig, setProviderConfig] = useState<ProviderConfig>({});
    // Keyed by each provider's configKey; a key is absent until /workspace/integrations answers.
    const [configured, setConfigured] = useState<Record<string, boolean>>({});
    const authAttempted = useRef(false);
    // Removes the in-flight OAuth popup listeners; replaced on a new attempt and run on unmount.
    const oauthCleanupRef = useRef<(() => void) | null>(null);
    useEffect(() => () => { oauthCleanupRef.current?.(); }, []);

    // One `browseFolders` state covers SharePoint's three steps (sites, drives, folders).
    type FolderEntry = { id: string; name: string; subtitle?: string };
    const [browsePath, setBrowsePath] = useState<FolderEntry[]>([]);
    const [browseFolders, setBrowseFolders] = useState<FolderEntry[]>([]);
    const [browseFilesCount, setBrowseFilesCount] = useState(0);
    const [browseLoading, setBrowseLoading] = useState(false);
    const [browseError, setBrowseError] = useState<string | null>(null);
    const [spSearch, setSpSearch] = useState('');
    const [accountInfo, setAccountInfo] = useState<{ name?: string; email?: string } | null>(null);

    useEffect(() => {
        authFetch('/workspace/integrations')
            .then(res => res.ok ? res.json() : null)
            .catch(() => null)
            .then(data => {
                setConfigured(Object.fromEntries(
                    Object.values(AUTH_PROVIDERS).map(p => [p.configKey, data?.[p.configKey] ?? false]),
                ));
            });
    }, []);


    useEffect(() => {
        if (!editSource) return;
        setSourceType(editSource.source_type || 'web');
        if (AUTH_PROVIDERS[editSource.source_type] && editSource.config?.credentials) {
            setCredentials(editSource.config.credentials);
            const cfg: ProviderConfig = {};
            if (editSource.config.folder_id) {
                cfg.folder_id = editSource.config.folder_id;
                cfg.folder_name = editSource.config.folder_name || editSource.config.folder_id;
            }
            if (editSource.config.site_id) {
                cfg.site_id = editSource.config.site_id;
                cfg.site_name = editSource.config.site_name || '';
            }
            if (editSource.config.drive_id) {
                cfg.drive_id = editSource.config.drive_id;
                cfg.drive_name = editSource.config.drive_name || '';
            }
            // DocuSign's regional API host (base_uri) is carried in drive_id for /auth/browse.
            if (editSource.source_type === 'docusign' && editSource.config.base_uri) {
                cfg.base_uri = editSource.config.base_uri;
                cfg.drive_id = editSource.config.base_uri;
            }
            setProviderConfig(cfg);
        }
    }, [editSource]);

    // Edit mode calls setSourceType directly, keeping its provider state.
    const changeSourceType = (newType: string) => {
        setSourceType(newType);
        setCredentials(null);
        setProviderConfig({});
        setBrowsePath([]);
        setBrowseFolders([]);
        setBrowseFilesCount(0);
        setBrowseError(null);
        setSpSearch('');
        setAccountInfo(null);
        authAttempted.current = false;
    };

    // A later ?source= (the agent moving the person on from this page) switches the form.
    const lastAsked = useRef(askedSource);
    useEffect(() => {
        if (editSource || askedSource === lastAsked.current) return;
        lastAsked.current = askedSource;
        if (offered(askedSource)) changeSourceType(askedSource);
    }, [askedSource]);

    useEffect(() => {
        if (!credentials || !AUTH_PROVIDERS[sourceType]) { setAccountInfo(null); return; }
        let stale = false;
        authFetch('/auth/account-info', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ access_token: credentials.token, source_type: sourceType }),
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (!stale) setAccountInfo(data); })
            .catch(() => {});
        return () => { stale = true; };
    }, [credentials, sourceType]);

    const isSharePoint = sourceType === 'sharepoint';
    const isDocusign = sourceType === 'docusign';
    const hasSite = (isSharePoint || isDocusign) && !!providerConfig.site_id;
    const hasDrive = (isSharePoint || isDocusign) && !!providerConfig.drive_id;
    const spShowFolders = isSharePoint
        ? (hasSite && hasDrive)
        : isDocusign
            ? hasSite
            : true;

    // True when the provider returns a flat list (e.g. Xero orgs) for a single-select picker.
    const isFlatPicker = !!AUTH_PROVIDERS[sourceType]?.flatPicker;
    const isNoPicker = !!AUTH_PROVIDERS[sourceType]?.noPicker;

    useEffect(() => {
        if (!credentials || !AUTH_PROVIDERS[sourceType] || isNoPicker) return;

        let stale = false;
        setBrowseLoading(true);
        setBrowseError(null);

        const body: Record<string, any> = {
            access_token: credentials.token,
            source_type: sourceType,
        };
        let step: 'sp-sites' | 'sp-drives' | 'folders';
        if ((isSharePoint || isDocusign) && !hasSite) {
            step = 'sp-sites';
            if (spSearch) body.search = spSearch;
        } else if (isSharePoint && !hasDrive) {
            step = 'sp-drives';
            body.site_id = providerConfig.site_id;
        } else {
            step = 'folders';
            body.folder_id = browsePath.length > 0 ? browsePath[browsePath.length - 1].id : '';
            if (isSharePoint || isDocusign) {
                body.site_id = providerConfig.site_id;
                body.drive_id = providerConfig.drive_id;
            }
        }

        authFetch('/auth/browse', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        })
            .then(async res => {
                if (stale) return null;
                if (res.status === 401) { handleAuthExpired(); return null; }
                if (!res.ok) {
                    const data = await res.json().catch(() => null);
                    throw new Error(data?.detail || `Failed to browse ${AUTH_PROVIDERS[sourceType].label}`);
                }
                return res.json();
            })
            .then(data => {
                if (stale || !data) return;
                if (step === 'sp-sites') {
                    setBrowseFolders((data.sites ?? []).map((s: any) => ({ id: s.id, name: s.name, subtitle: s.url })));
                    setBrowseFilesCount(0);
                } else if (step === 'sp-drives') {
                    const drives: FolderEntry[] = (data.drives ?? []).map((d: any) => ({ id: d.id, name: d.name }));
                    setBrowseFolders(drives);
                    setBrowseFilesCount(0);
                    if (drives.length === 1) {
                        setProviderConfig(c => ({ ...c, drive_id: drives[0].id, drive_name: drives[0].name }));
                    }
                } else {
                    setBrowseFolders(data.folders);
                    setBrowseFilesCount(data.files_count);
                    // Flat picker with exactly one item: auto-select it.
                    if (isFlatPicker && data.folders?.length === 1) {
                        const only = data.folders[0];
                        setProviderConfig(c => ({
                            ...c,
                            folder_id: only.id,
                            folder_name: only.name,
                        }));
                    }
                }
            })
            .catch(e => { if (!stale) setBrowseError(e.message); })
            .finally(() => { if (!stale) setBrowseLoading(false); });

        return () => { stale = true; };
    }, [credentials, sourceType, browsePath, hasSite, hasDrive, providerConfig.site_id, providerConfig.drive_id, spSearch]);

    // Flat-picker providers set folder_id themselves, so this sync applies to folder browsers (SharePoint once in its folders step).
    useEffect(() => {
        if (!AUTH_PROVIDERS[sourceType]) return;
        if (isFlatPicker) return;
        if ((isSharePoint || isDocusign) && !spShowFolders) return;
        const last = browsePath[browsePath.length - 1];
        const fallbackName = isSharePoint
            ? (providerConfig.drive_name ?? '')
            : isDocusign
                ? (providerConfig.site_name ?? '')
                : (AUTH_PROVIDERS[sourceType]?.label ?? '');
        setProviderConfig(c => ({
            ...c,
            folder_id: last?.id ?? '',
            folder_name: last?.name ?? fallbackName,
        }));
    }, [browsePath, sourceType, spShowFolders]);

    const handleHelpClick = (documentation: string) => {
        openPanel(
            'Setup Guide',
            <HelpPanelContent
                documentation={documentation}
            />
        );
    };

    // `credentials` is a dep so that when handleAuthExpired wipes them (e.g. /auth/browse returned 401)
    // this effect re-fires and launches a fresh login.
    useEffect(() => {
        const provider = AUTH_PROVIDERS[sourceType];
        if (!provider) { authAttempted.current = false; return; }
        if (credentials || isEditMode || authAttempted.current) return;
        if (configured[provider.configKey] !== true) return;

        authAttempted.current = true;
        const cached = localStorage.getItem(provider.storageKey);
        if (cached) {
            try {
                const data = JSON.parse(cached);
                if (data?.type === provider.messageType && data.credentials?.token) {
                    setCredentials(data.credentials);
                    return;
                }
            } catch { }
        }
        handleConnect();
    }, [sourceType, configured, credentials]);

    const handleConnect = async () => {
        const provider = AUTH_PROVIDERS[sourceType];
        if (!provider) return;

        setError(null);
        oauthCleanupRef.current?.();

        try {
            const sep = provider.loginPath.includes('?') ? '&' : '?';
            const res = await authFetch(`${provider.loginPath}${sep}redirect_origin=${encodeURIComponent(window.location.origin)}`);
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.detail || 'Failed to get auth URL');
            }
            const { auth_url } = await res.json();

            oauthCleanupRef.current = runOAuthPopup({
                url: auth_url,
                name: provider.popupName,
                storageKey: provider.storageKey,
                messageType: provider.messageType,
                onResult: (data) => {
                    oauthCleanupRef.current = null;
                    if (data?.type === provider.messageType) {
                        setCredentials(data.credentials);
                        showToast(`${provider.label} connected successfully!`, 'success');
                    } else if (data?.type === `${provider.messageType}-error`) {
                        setError(data.error || 'Authentication failed');
                    }
                },
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : `Failed to connect to ${provider.label}`);
        }
    };

    // Called by OAuth browsers when the access token is rejected.
    const handleAuthExpired = () => {
        const provider = AUTH_PROVIDERS[sourceType];
        setCredentials(null);
        if (provider) localStorage.removeItem(provider.storageKey);
        authAttempted.current = false;
    };


    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setUploading(true);
        setError(null);

        try {
            const formData = new FormData(e.currentTarget);
            const source = sourceType === 'sharepoint' && providerConfig.site_name
                ? `${providerConfig.site_name}${providerConfig.drive_name ? ' / ' + providerConfig.drive_name : ''}${providerConfig.folder_id ? ' / ' + providerConfig.folder_name : ''}`
                : sourceType === 'docusign' && providerConfig.site_name
                    ? `${providerConfig.site_name}${providerConfig.folder_name ? ' / ' + providerConfig.folder_name : ''}`
                    : providerConfig.folder_name
                        ? providerConfig.folder_name
                        : (formData.get('source') as string)?.trim();

            const config: Record<string, any> = {};
            const fields = sourceTypeFields[sourceType] || [];

            fields.forEach(field => {
                if (field.id === 'source') return;

                const value = formData.get(field.id);

                if (field.type === 'checkbox') {
                    config[field.id] = value === 'on';
                } else if (field.type === 'number') {
                    config[field.id] = parseInt(value as string) || field.defaultValue || 0;
                } else if (field.id === 'channel_ids') {
                    config[field.id] = (value as string).split(',').map(s => s.trim());
                } else if (field.id === 'credentials') {
                    try {
                        config[field.id] = JSON.parse(value as string);
                    } catch (e) {
                        throw new Error('Invalid JSON format for credentials');
                    }
                } else {
                    config[field.id] = (value as string)?.trim();
                }
            });

            if (AUTH_PROVIDERS[sourceType] && credentials) {
                config.credentials = credentials;
                if (providerConfig.folder_id !== undefined) config.folder_id = providerConfig.folder_id;
                if (providerConfig.folder_name !== undefined) config.folder_name = providerConfig.folder_name;

                if (sourceType === 'sharepoint') {
                    config.site_id = providerConfig.site_id;
                    config.site_name = providerConfig.site_name;
                    config.drive_id = providerConfig.drive_id;
                    config.drive_name = providerConfig.drive_name;
                    config.folder_id = providerConfig.folder_id || '';
                    config.folder_name = providerConfig.folder_name || providerConfig.drive_name;
                } else if (sourceType === 'docusign') {
                    // site_id is the DocuSign account_id; drive_id carries its base_uri.
                    config.site_id = providerConfig.site_id;
                    config.site_name = providerConfig.site_name;
                    config.base_uri = providerConfig.base_uri || providerConfig.drive_id;
                    config.folder_id = providerConfig.folder_id || '';
                    config.folder_name = providerConfig.folder_name || '';
                } else if (sourceType === 'box') {
                    // Box root is folder id "0"
                    config.folder_id = providerConfig.folder_id || '0';
                }
            }

            const payload = {
                source_type: sourceType,
                source,
                config,
            };

            if (isEditMode) {
                const response = await authFetch(`/sources/${editSource!.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to update source');
                }

                resetForm();
                showToast('Data source updated', 'success');

                if (onSuccess) onSuccess();
            } else {
                const response = await authFetch('/sources', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to queue ingestion');
                }

                const result = await response.json();

                resetForm();
                // A source searched live has no sync, so creating it starts no job.
                showToast(result.job_id ? 'Data source queued for ingestion' : 'Data source connected', 'success');

                if (redirectTo) {
                    navigate(redirectTo);
                } else if (result.source_id) {
                    navigate(result.job_id ? `/sources/${result.source_id}?tab=jobs` : `/sources/${result.source_id}`);
                } else if (result.job_id) {
                    navigate(`/jobs/${result.job_id}`);
                }

                if (onSuccess) onSuccess();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to submit');
        } finally {
            setUploading(false);
        }
    };

    const resetForm = () => {
        setSourceType('');
        setCredentials(null);
        setProviderConfig({});
        authAttempted.current = false;
        setError(null);
    };

    // Providers that need a folder selected before submit; dropbox/box/notion/outlook allow root, sharepoint needs site + drive.
    const FOLDER_REQUIRED = new Set(['onedrive', 'drive', 'gmail', 'xero', 'hubspot']);
    const submitDisabled = (() => {
        if (uploading || !sourceType) return true;
        if (!AUTH_PROVIDERS[sourceType]) return false;
        if (!credentials) return true;
        if (browseLoading) return true;
        if (sourceType === 'sharepoint') return !providerConfig.site_id || !providerConfig.drive_id;
        if (sourceType === 'docusign') return !providerConfig.site_id || !providerConfig.folder_id;
        if (sourceType === 'drive' && providerConfig.folder_id === 'sharedWithMe') return true;
        if (FOLDER_REQUIRED.has(sourceType) && !providerConfig.folder_id) return true;
        return false;
    })();

    const submitLabel = (() => {
        if (isEditMode) return 'Update Source';
        if (isNoPicker) return `Connect ${AUTH_PROVIDERS[sourceType].label}`;
        if (sourceType === 'sharepoint' && providerConfig.site_name) {
            const path = `${providerConfig.site_name}${providerConfig.drive_name ? ' / ' + providerConfig.drive_name : ''}${providerConfig.folder_id ? ' / ' + providerConfig.folder_name : ''}`;
            return `Index '${path}'`;
        }
        if (isFlatPicker && providerConfig.folder_name) return `Connect ${providerConfig.folder_name}`;
        if (providerConfig.folder_name) return `Index '${providerConfig.folder_name.split('/').pop()}'`;
        if (sourceType === 'outlook') return "Index 'All mail'";
        if (sourceType === 'stripe') return 'Connect Stripe';
        if (sourceType === 'joiin') return 'Connect Joiin';
        if (sourceType === 'companies_house') return 'Connect Companies House';
        return 'Index Data Source';
    })();

    return (
        <>
            <div className="flex-1 flex flex-col justify-between">
                {error && <InlineError message={error} className="mb-4" />}

                {!isEditMode && (
                    <>
                        <SourceTypeCombobox
                            items={sourceTypes}
                            selected={sourceType}
                            onSelect={changeSourceType}
                            onClear={resetForm}
                            typeFilter={typeFilter}
                            onTypeFilterChange={updateTypeFilter}
                            getDisabled={(id) => {
                                const type = sourceTypes.find(t => t.id === id);
                                if (type?.comingSoon) return { reason: 'Coming Soon' };
                                const provider = AUTH_PROVIDERS[id];
                                if (provider && configured[provider.configKey] === false) {
                                    return { reason: `${provider.label} integration not configured` };
                                }
                                return null;
                            }}
                        />
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1 border-t border-softgrey" />
                        </div>
                    </>
                )}

                {(isFlatPicker || isNoPicker) && !credentials && (
                    <div className="mb-3">
                        <button
                            type="button"
                            onClick={handleConnect}
                            className="w-full py-3 px-4 bg-brand text-white rounded-lg font-semibold hover:bg-brand-dark flex items-center justify-center gap-2"
                        >
                            <ExternalLink className="h-4 w-4 shrink-0" />
                            Connect to {AUTH_PROVIDERS[sourceType].label}
                        </button>
                    </div>
                )}

                {(isFlatPicker || isNoPicker) && credentials && (() => {
                    const provider = AUTH_PROVIDERS[sourceType];
                    const pickerLabel = provider.flatPickerLabel ?? 'item';
                    const items = browseFolders;
                    return (
                        <div className="mb-3 p-4 border border-softgrey rounded-[10px] bg-white">
                            <div className={`flex items-center gap-3 ${isNoPicker ? '' : 'mb-3'}`}>
                                <SourceIcon id={sourceType} label={provider.label} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[15px] font-medium text-graphite truncate">
                                        Connected to {provider.label}
                                    </div>
                                    {accountInfo?.name && (
                                        <div className="text-xs text-coolslate truncate">
                                            {accountInfo.name}{accountInfo.email ? ` · ${accountInfo.email}` : ''}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { handleAuthExpired(); handleConnect(); }}
                                    className="text-xs text-brand hover:underline"
                                >
                                    Switch account
                                </button>
                            </div>
                            {isNoPicker ? null : browseLoading ? (
                                <div className="flex items-center gap-2 text-sm text-coolslate">
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Loading {pickerLabel}s…
                                </div>
                            ) : browseError ? (
                                <div className="text-sm text-state-error">{browseError}</div>
                            ) : items.length === 0 ? (
                                <div className="text-sm text-coolslate">
                                    No {pickerLabel}s found. You may need to grant access on the consent screen.
                                </div>
                            ) : items.length === 1 ? (
                                <div className="text-sm text-graphite">
                                    {pickerLabel.charAt(0).toUpperCase() + pickerLabel.slice(1)}: <span className="font-medium">{items[0].name}</span>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs font-medium text-coolslate mb-1.5">
                                        Select {pickerLabel}
                                    </label>
                                    <select
                                        value={providerConfig.folder_id ?? ''}
                                        onChange={e => {
                                            const item = items.find(i => i.id === e.target.value);
                                            setProviderConfig(c => ({
                                                ...c,
                                                folder_id: item?.id ?? '',
                                                folder_name: item?.name ?? '',
                                            }));
                                        }}
                                        className="w-full text-sm px-3 py-2 border border-softgrey rounded-md bg-white text-graphite focus:outline-none focus:border-brand"
                                    >
                                        <option value="">Select a {pickerLabel}…</option>
                                        {items.map(item => (
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {AUTH_PROVIDERS[sourceType] && !isFlatPicker && !isNoPicker && (
                    <div className="mb-3">
                        {credentials && !browseError ? (() => {
                            // SharePoint path model: [site?, drive?, ...folders]; onNavigate interprets its length.
                            const handleSPNavigate = (newPath: FolderEntry[]) => {
                                if (newPath.length === 0) {
                                    setProviderConfig({});
                                    setBrowsePath([]);
                                } else if (newPath.length === 1) {
                                    const [site] = newPath;
                                    setProviderConfig({ site_id: site.id, site_name: site.name });
                                    setBrowsePath([]);
                                } else if (newPath.length === 2) {
                                    const [site, drive] = newPath;
                                    setProviderConfig({
                                        site_id: site.id, site_name: site.name,
                                        drive_id: drive.id, drive_name: drive.name,
                                    });
                                    setBrowsePath([]);
                                } else {
                                    const [, , ...folders] = newPath;
                                    setBrowsePath(folders);
                                }
                            };

                            // DocuSign path model: [account, ...folders]; the account's base_uri rides along on account.subtitle.
                            const handleDocusignNavigate = (newPath: FolderEntry[]) => {
                                if (newPath.length === 0) {
                                    setProviderConfig({});
                                    setBrowsePath([]);
                                } else if (newPath.length === 1) {
                                    const [account] = newPath;
                                    setProviderConfig({
                                        site_id: account.id,
                                        site_name: account.name,
                                        drive_id: account.subtitle ?? '',
                                        drive_name: '',
                                        base_uri: account.subtitle ?? '',
                                    });
                                    setBrowsePath([]);
                                } else {
                                    const [, ...folders] = newPath;
                                    setBrowsePath(folders);
                                }
                            };

                            let path: FolderEntry[] = browsePath;
                            let emptyMessage: string | undefined;
                            let title: string | undefined;
                            let rootLabel: string | undefined;
                            let search: { value: string; onChange: (v: string) => void; placeholder?: string } | undefined;
                            let onNavigate: (p: FolderEntry[]) => void = setBrowsePath;

                            if (isSharePoint && !hasSite) {
                                path = [];
                                emptyMessage = 'No sites found';
                                title = 'Select a SharePoint site';
                                search = { value: spSearch, onChange: setSpSearch, placeholder: 'Search sites...' };
                                onNavigate = handleSPNavigate;
                            } else if (isSharePoint && !hasDrive) {
                                const site: FolderEntry = { id: providerConfig.site_id!, name: providerConfig.site_name ?? '' };
                                path = [site];
                                emptyMessage = 'No document libraries found';
                                rootLabel = 'Sites';
                                onNavigate = handleSPNavigate;
                            } else if (isSharePoint) {
                                const site: FolderEntry = { id: providerConfig.site_id!, name: providerConfig.site_name ?? '' };
                                const drive: FolderEntry = { id: providerConfig.drive_id!, name: providerConfig.drive_name ?? '' };
                                path = [site, drive, ...browsePath];
                                rootLabel = 'Sites';
                                onNavigate = handleSPNavigate;
                            } else if (isDocusign && !hasSite) {
                                path = [];
                                emptyMessage = 'No DocuSign accounts found';
                                title = 'Select a DocuSign account';
                                onNavigate = handleDocusignNavigate;
                            } else if (isDocusign) {
                                const account: FolderEntry = { id: providerConfig.site_id!, name: providerConfig.site_name ?? '' };
                                path = [account, ...browsePath];
                                rootLabel = 'Accounts';
                                onNavigate = handleDocusignNavigate;
                            }

                            return (
                                <SourceBrowser
                                    folders={browseFolders}
                                    path={path}
                                    filesCount={browseFilesCount}
                                    loading={browseLoading}
                                    error={browseError}
                                    emptyMessage={emptyMessage}
                                    title={title}
                                    rootLabel={rootLabel}
                                    search={search}
                                    onNavigate={onNavigate}
                                    accountInfo={accountInfo}
                                    onSignOut={handleAuthExpired}
                                    onSignInDifferent={() => { handleAuthExpired(); handleConnect(); }}
                                />
                            );
                        })() : (
                            <>
                                {browseError && <InlineError message={browseError} className="mb-3" />}
                                <button
                                    type="button"
                                    onClick={handleConnect}
                                    className="w-full py-3 px-4 bg-brand text-white rounded-lg font-semibold hover:bg-brand-dark flex items-center justify-center gap-2"
                                >
                                    <ExternalLink className="h-4 w-4 shrink-0"/>Connect to {AUTH_PROVIDERS[sourceType].label}
                                </button>
                            </>
                        )}
                    </div>
                )}

                <form id="source-form" onSubmit={handleFormSubmit} className="space-y-4">
                    {sourceTypeFields[sourceType]?.map((field) => {
                                const defaultValue = isEditMode && editSource
                                    ? field.id === 'source'
                                        ? editSource.source
                                        : editSource.config?.[field.id] ?? field.defaultValue
                                    : field.defaultValue;

                                return (
                                    <div key={field.id}>
                                        {field.type === 'checkbox' ? (
                                            <>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name={field.id}
                                                        defaultChecked={defaultValue as boolean}
                                                        autoComplete="off"
                                                        className="h-4 w-4 text-brand focus:ring-brand border-softgrey rounded"
                                                    />
                                                    <span className="text-sm font-medium text-graphite">{field.label}</span>
                                                </label>
                                                {field.helperText && (
                                                    <p className="text-xs text-coolslate mt-1 ml-6">{field.helperText}</p>
                                                )}
                                            </>
                                        ) : field.type === 'textarea' ? (
                                            <>
                                                <label htmlFor={field.id} className="block text-sm font-medium text-graphite mb-2">
                                                    {field.label}
                                                </label>
                                                <textarea
                                                    id={field.id}
                                                    name={field.id}
                                                    placeholder={field.placeholder}
                                                    required={field.required}
                                                    defaultValue={defaultValue as string}
                                                    rows={5}
                                                    autoComplete="off"
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck="false"
                                                    className="w-full px-4 py-2 border border-softgrey rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono text-sm"
                                                />
                                                <div className="flex justify-between w-full items-center gap-2 mt-1">
                                                    <p className="text-xs text-coolslate">{field.helperText}</p>
                                                    {field.documentation && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleHelpClick(field.documentation!)}
                                                            className="text-brand hover:text-brand-dark cursor-pointer transition-colors"
                                                            title="View detailed documentation"
                                                        >
                                                            <HelpCircle className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <label htmlFor={field.id} className="block text-sm font-medium text-graphite mb-2">
                                                    {field.label}
                                                </label>
                                                <input
                                                    id={field.id}
                                                    type={field.type}
                                                    name={field.id}
                                                    placeholder={field.placeholder}
                                                    required={field.required}
                                                    min={field.min}
                                                    max={field.max}
                                                    defaultValue={defaultValue as string | number}
                                                    autoComplete="off"
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck="false"
                                                    className="w-full px-4 py-2 border border-softgrey rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                                                />
                                                <div className="flex justify-between w-full items-center gap-2 mt-1">
                                                    <p className="text-xs text-coolslate">{field.helperText}</p>
                                                    {field.documentation && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleHelpClick(field.documentation!)}
                                                            className="text-brand hover:text-brand-dark cursor-pointer transition-colors"
                                                            title="View detailed documentation"
                                                        >
                                                            <HelpCircle className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                </form>
            </div>

            <div className="mt-6">
                <button
                    type="submit"
                    form="source-form"
                    disabled={submitDisabled}
                    className="w-full py-3 px-4 bg-brand text-white rounded-lg font-semibold hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {uploading ? (
                        <>
                            <Loader className="w-4 h-4 animate-spin" />
                            {isEditMode ? 'Updating...' : 'Processing...'}
                        </>
                    ) : submitLabel}
                </button>
            </div>

        </>
    );
}
